import { useMemo } from 'react'
import { ChartCard } from './ChartCard'
import type { AggregatedChartData } from '../types'
import { getChartColor } from '../constants/chartColors'

export interface KpiBadgeChartProps {
  readonly data: ReadonlyArray<AggregatedChartData>
  readonly title?: string
  readonly subtitle?: string
}

/**
 * KPI Badge Chart - Boolean flag visualization
 *
 * Displays two large badges showing Yes/No counts with percentages.
 * Replaces donut charts for boolean columns (verified, famous, isPerson, processed).
 *
 * Features:
 * - Clean, space-efficient design
 * - Large percentage values (Outfit font)
 * - Color-coded states (Green for Yes, Gray for No)
 * - Capsule DNA maintained with rounded corners
 */
export const KpiBadgeChart: React.FC<KpiBadgeChartProps> = ({ data, title = 'KPI', subtitle }) => {
  const stats = useMemo(() => {
    // Find Yes/No or True/False counts
    const yesItem = data.find(
      (d) =>
        d.label?.toLowerCase() === 'yes' ||
        d.label?.toLowerCase() === 'true' ||
        d.label === '1'
    )
    const noItem = data.find(
      (d) =>
        d.label?.toLowerCase() === 'no' ||
        d.label?.toLowerCase() === 'false' ||
        d.label === '0'
    )

    const yesCount = yesItem?.value || 0
    const noCount = noItem?.value || 0
    const total = yesCount + noCount

    if (total === 0) {
      return { yesCount: 0, noCount: 0, yesPercent: 0, noPercent: 0, total: 0 }
    }

    const yesPercent = Number(((yesCount / total) * 100).toFixed(1))
    const noPercent = Number(((noCount / total) * 100).toFixed(1))

    return { yesCount, noCount, yesPercent, noPercent, total }
  }, [data])

  if (stats.total === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle}>
        <div className="flex items-center justify-center min-h-[160px] text-v11-text-muted text-sm">
          No data available for this period
        </div>
      </ChartCard>
    )
  }

  // Use Felton Chart DNA colors: Emerald for Yes (index 5), Slate for No (index 7)
  const yesColor = getChartColor(5) // Emerald - Success
  const noColor = getChartColor(7)  // Slate - Neutral

  return (
    <ChartCard title={title} subtitle={subtitle}>
      <div className="flex items-center justify-center gap-3 sm:gap-4 md:gap-6 py-4 sm:py-6 md:py-8">
        {/* Yes Badge */}
        <div className="flex flex-col items-center flex-1 min-w-0 max-w-[140px] sm:max-w-[160px] md:max-w-[180px]">
          <div className="relative group w-full">
            {/* Badge container with Capsule DNA and Felton Emerald */}
            <div
              className="border-2 rounded-xl sm:rounded-2xl px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-4 transition-all duration-300 w-full overflow-hidden"
              style={{
                backgroundColor: `hsl(${yesColor.cssVar} / 0.1)`,
                borderColor: `hsl(${yesColor.cssVar} / 0.3)`,
              }}
            >
              {/* Percentage value - Responsive font size with scale for fit */}
              <div
                className="text-center text-base sm:text-lg md:text-xl lg:text-2xl font-display font-bold tabular-nums"
                style={{
                  color: `hsl(${yesColor.cssVar})`,
                  transform: stats.yesPercent >= 99 ? 'scale(0.85)' : stats.yesPercent >= 10 ? 'scale(0.95)' : 'scale(1)',
                  transformOrigin: 'center'
                }}
              >
                {stats.yesPercent}%
              </div>
              {/* Label and count */}
              <div className="mt-0.5 sm:mt-1 text-center overflow-hidden">
                <div
                  className="text-[10px] sm:text-xs font-medium truncate"
                  style={{ color: `hsl(${yesColor.cssVar})` }}
                >
                  Yes
                </div>
                <div
                  className="text-[9px] sm:text-[10px] tabular-nums truncate"
                  style={{ color: `hsl(${yesColor.cssVar} / 0.7)` }}
                >
                  ({stats.yesCount.toLocaleString()})
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-16 sm:h-20 w-px bg-v11-border flex-shrink-0" />

        {/* No Badge */}
        <div className="flex flex-col items-center flex-1 min-w-0 max-w-[140px] sm:max-w-[160px] md:max-w-[180px]">
          <div className="relative group w-full">
            {/* Badge container with Capsule DNA and Felton Slate */}
            <div
              className="border-2 rounded-xl sm:rounded-2xl px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-4 transition-all duration-300 w-full overflow-hidden"
              style={{
                backgroundColor: `hsl(${noColor.cssVar} / 0.1)`,
                borderColor: `hsl(${noColor.cssVar} / 0.3)`,
              }}
            >
              {/* Percentage value - Responsive font size with scale for fit */}
              <div
                className="text-center text-base sm:text-lg md:text-xl lg:text-2xl font-display font-bold tabular-nums"
                style={{
                  color: `hsl(${noColor.cssVar})`,
                  transform: stats.noPercent >= 99 ? 'scale(0.85)' : stats.noPercent >= 10 ? 'scale(0.95)' : 'scale(1)',
                  transformOrigin: 'center'
                }}
              >
                {stats.noPercent}%
              </div>
              {/* Label and count */}
              <div className="mt-0.5 sm:mt-1 text-center overflow-hidden">
                <div
                  className="text-[10px] sm:text-xs font-medium truncate"
                  style={{ color: `hsl(${noColor.cssVar})` }}
                >
                  No
                </div>
                <div
                  className="text-[9px] sm:text-[10px] tabular-nums truncate"
                  style={{ color: `hsl(${noColor.cssVar} / 0.7)` }}
                >
                  ({stats.noCount.toLocaleString()})
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ChartCard>
  )
}
